import { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Save, CheckCircle2, Sparkles, ChevronDown, Wallet } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Reveal } from "./Reveal";
import { SliderInput } from "./SliderInput";
import { ScoreGauge } from "./ScoreGauge";
import { DEFAULT_INPUTS, FIELDS, analyze, scoreVerdict, CCAA_RATES } from "@/lib/finance";
import { eur, pct } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRate = (v) => `${v.toLocaleString("es-ES")} %`;

const TONE_PILL = {
  emerald: "bg-emerald-500 text-white",
  green: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  red: "bg-red-100 text-red-600",
};

const ResultTile = ({ label, value, positive, testid }) => (
  <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 font-mono text-xl font-bold tabular-nums ${positive === true ? "text-emerald-600" : positive === false ? "text-red-500" : "text-slate-900"}`}>
      {value}
    </p>
  </div>
);

const DesembolsoRow = ({ label, value, testid }) => (
  <div className="flex items-center justify-between px-4 py-2.5" data-testid={testid}>
    <dt className="text-sm text-slate-600">{label}</dt>
    <dd className="font-mono text-sm font-semibold tabular-nums text-slate-900">{value}</dd>
  </div>
);

export const Calculator = () => {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [ccaa, setCcaa] = useState("");
  const [saving, setSaving] = useState(false);
  const r = useMemo(() => analyze(inputs), [inputs]);
  const verdict = scoreVerdict(r.score);
  const selectedCcaa = CCAA_RATES.find((c) => c.id === ccaa);

  const onChange = (key, value) => setInputs((s) => ({ ...s, [key]: value }));

  const onCcaa = (id) => {
    setCcaa(id);
    const rate = CCAA_RATES.find((c) => c.id === id);
    if (rate) {
      setInputs((s) => ({ ...s, itpPct: rate.itp }));
    }
  };

  const chartData = [
    { name: "Ingresos", v: Math.round(inputs.alquiler), fill: "#0F172A" },
    { name: "Gastos", v: -Math.round(inputs.gastosAnuales / 12 + r.gestionMensual), fill: "#F59E0B" },
    { name: "Hipoteca", v: -Math.round(r.cuota), fill: "#6366F1" },
    { name: "Cash flow", v: Math.round(r.cfMensual), fill: r.cfMensual >= 0 ? "#10B981" : "#EF4444" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/analyses`, {
        inputs: { ...inputs, ccaa: ccaa || null },
        results: {
          rentabilidadBruta: +r.bruta.toFixed(2),
          rentabilidadNeta: +r.neta.toFixed(2),
          cashFlowMensual: +r.cfMensual.toFixed(2),
          cashOnCash: +r.cashOnCash.toFixed(2),
          inversionInicial: Math.round(r.inversionInicial),
          score: r.score,
        },
      });
      toast.success("Análisis guardado", { description: "Se ha registrado de forma anónima para estadísticas agregadas." });
    } catch {
      toast.error("No se pudo guardar el análisis");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="analisis" data-testid="interactive-calculator-section" className="bg-white py-16 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12 max-w-3xl sm:mb-16">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Calculadora</p>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Analiza cualquier inmueble
          </h2>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">
            Introduce el precio de compra, los impuestos y gastos, la hipoteca, la reforma y el alquiler estimado: el motor calcula al instante la rentabilidad real de un piso en España.
          </p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr] lg:gap-8">
          <Reveal className="rounded-[2rem] border border-slate-200 bg-slate-50/50 p-6 sm:p-8">
            <div className="mb-7 flex items-center gap-2.5">
              <Sparkles className="h-5 w-5 text-emerald-500" />
              <h3 className="font-display text-lg font-bold text-slate-900">Datos del inmueble</h3>
            </div>
            <div className="mb-7 space-y-2.5">
              <label htmlFor="ccaa" className="text-sm font-medium text-slate-600">
                Comunidad Autónoma
              </label>
              <div className="relative">
                <select
                  id="ccaa"
                  data-testid="select-comunidad-autonoma"
                  value={ccaa}
                  onChange={(e) => onCcaa(e.target.value)}
                  className="w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm font-medium text-slate-900 outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value="">Elige para aplicar el ITP automáticamente…</option>
                  {CCAA_RATES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} — ITP {formatRate(c.itp)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </div>
              {selectedCcaa && (
                <p data-testid="itp-auto-hint" className="rounded-xl bg-emerald-50 px-3.5 py-2.5 text-xs leading-relaxed text-emerald-800">
                  ITP de vivienda usada en {selectedCcaa.name}: {formatRate(selectedCcaa.itp)}, aplicado
                  automáticamente{selectedCcaa.note ? ` (${selectedCcaa.note})` : ""}. Notaría, registro y gestoría los
                  defines tú en el campo correspondiente.
                </p>
              )}
            </div>
            <div className="space-y-7">
              {FIELDS.map((f) => (
                <SliderInput key={f.key} field={f} value={inputs[f.key]} onChange={onChange} />
              ))}
            </div>
            <p className="mt-8 text-xs leading-relaxed text-slate-400">
              Estimación orientativa. No constituye asesoramiento financiero. El ITP se aplica sobre vivienda usada
              (tipo general 2026); la obra nueva tributa por IVA. Los tipos pueden variar según bonificaciones y tu
              situación personal.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="lg:sticky lg:top-24 lg:self-start">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-900/5">
              <div className="bg-slate-900 px-6 pb-8 pt-7 text-center sm:px-8">
                <ScoreGauge score={r.score} tone={verdict.tone} light />
                <span data-testid="result-verdict-pill" className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold ${TONE_PILL[verdict.tone]}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> {verdict.label}
                </span>
              </div>

              <div className="p-6 sm:p-8">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <ResultTile label="Rent. bruta" value={pct(r.bruta)} testid="result-rentabilidad-bruta" />
                  <ResultTile label="Rent. neta" value={pct(r.neta)} positive={r.neta >= 4 ? true : undefined} testid="result-rentabilidad-neta" />
                  <ResultTile label="Cash flow" value={`${eur(r.cfMensual)}/mes`} positive={r.cfMensual >= 0} testid="result-cash-flow-mensual" />
                  <ResultTile label="Cash-on-cash" value={pct(r.cashOnCash)} positive={r.cashOnCash >= 8 ? true : undefined} testid="result-cash-on-cash" />
                  <ResultTile label="Gestión alquiler" value={`${eur(r.gestionMensual)}/mes`} testid="result-gestion-alquiler" />
                  <ResultTile label="Cuota hipoteca" value={`${eur(r.cuota)}/mes`} testid="result-cuota-hipoteca" />
                </div>

                <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200" data-testid="result-desembolso-inicial">
                  <p className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50/80 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <Wallet className="h-3.5 w-3.5 text-emerald-600" /> Lo que pones de tu bolsillo el día de la compra
                  </p>
                  <dl className="divide-y divide-slate-100">
                    <DesembolsoRow label={`Entrada (${100 - inputs.financiacionPct} % del precio)`} value={eur(r.entrada)} testid="result-entrada" />
                    <DesembolsoRow label={`ITP (${formatRate(inputs.itpPct)})`} value={eur(r.itpEuro)} testid="result-itp-euros" />
                    <DesembolsoRow label="Notaría, registro y gestoría" value={eur(inputs.notariaGastos)} testid="result-notaria" />
                    <DesembolsoRow label="Reforma y equipamiento" value={eur(inputs.reforma)} testid="result-reforma" />
                  </dl>
                  <div className="flex items-center justify-between bg-emerald-50 px-4 py-3">
                    <span className="text-sm font-bold text-emerald-900">Desembolso inicial total</span>
                    <span data-testid="result-desembolso-total" className="font-mono text-base font-bold tabular-nums text-emerald-700">
                      {eur(r.inversionInicial)}
                    </span>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-100 p-3">
                  <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Balance mensual</p>
                  <div className="h-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }} interval={0} />
                        <Tooltip cursor={{ fill: "rgba(15,23,42,0.04)" }} formatter={(v) => eur(v)} />
                        <Bar dataKey="v" radius={[6, 6, 6, 6]}>
                          {chartData.map((d, i) => (
                            <Cell key={i} fill={d.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <button
                  data-testid="save-analysis-btn"
                  onClick={save}
                  disabled={saving}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Guardando…" : "Guardar análisis"}
                </button>
                <p className="mt-3 text-center text-xs text-slate-400">Anónimo · Sin registro</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
